import axios, { AxiosInstance } from 'axios';
import Toaster from '../utils/toaster';
import { AuthStore } from '../utils/auth-store';
import moment from 'moment';
import { CommonHelper } from '../utils/common-helper';
import {
    API_ROUTE_CI_DASHBOARD_REPORT_ANALYSIS,
    API_ROUTE_DOWNLOAD_CLIENT_DOCUMENT,
    API_ROUTE_EXPORT_CLIENT,
    API_ROUTE_EXPORT_SUPPORTCASE,
    API_ROUTE_FORGOT_PASSWORD,
    API_ROUTE_RESET_PASSWORD,
    API_ROUTE_SELF_REGISTER_SOLUTIONUSER,
    API_ROUTE_SELF_REGISTER_VERIFYEMAIL_BY_OTP,
    API_ROUTE_SELF_REGISTER_VERIFYEMAIL_RESEND_OTP,
    API_ROUTE_SELF_REGISTER_VERIFYEMAIL_SEND_OTP,
    API_ROUTE_SIGNIN,
    API_ROUTE_SIGNIN_GUEST,
    API_ROUTE_SOLUTION_DOWNLOAD_DOCUMENT,
    API_ROUTE_SUPPORT_CASE_DOWNLOAD_ATTACHMENT,
    API_ROUTE_VE_ANALYSIS_DOWNLOAD_DOCUMENT,
    API_ROUTE_VE_ANALYSIS_EXCEL_DOWNLOAD_KEYINPUTS,
    API_ROUTE_VE_DASHBOARD_REPORT_ANALYSIS
} from 'src/data-access/api-routes';
import { AlertDialog } from 'src/components/common/dialog-boxes/alert-dialog';
import { IAPIResult } from './models/response/api-result';

const ANNONYMUS_API_ROUTES = [
    API_ROUTE_SIGNIN,
    API_ROUTE_SIGNIN_GUEST,
    API_ROUTE_FORGOT_PASSWORD,
    API_ROUTE_RESET_PASSWORD,
    API_ROUTE_SELF_REGISTER_VERIFYEMAIL_SEND_OTP,
    API_ROUTE_SELF_REGISTER_VERIFYEMAIL_RESEND_OTP,
    API_ROUTE_SELF_REGISTER_VERIFYEMAIL_BY_OTP,
    API_ROUTE_SELF_REGISTER_SOLUTIONUSER,
];

const PASS_THROUGH_URLS = [
    API_ROUTE_EXPORT_CLIENT,
    API_ROUTE_VE_ANALYSIS_DOWNLOAD_DOCUMENT,
    API_ROUTE_SOLUTION_DOWNLOAD_DOCUMENT,
    API_ROUTE_SUPPORT_CASE_DOWNLOAD_ATTACHMENT,
    API_ROUTE_EXPORT_SUPPORTCASE,
    API_ROUTE_VE_DASHBOARD_REPORT_ANALYSIS,
    API_ROUTE_CI_DASHBOARD_REPORT_ANALYSIS,
    API_ROUTE_VE_ANALYSIS_EXCEL_DOWNLOAD_KEYINPUTS,
    API_ROUTE_DOWNLOAD_CLIENT_DOCUMENT
];

const GetITBAxiosInstance = (): AxiosInstance => {
    // Create an instance
    const axiosInstance: AxiosInstance = axios.create({
        baseURL: CommonHelper.getAPIBaseUrl(),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    //#region Request helpers
    const attachAuthHeader = (config: any) => {
        const auth = AuthStore.getAuth();

        if (auth) {
            const isAccessTokenValid = isTokenValid(auth.accessTokenValidTo);
            if (isAccessTokenValid)
                config.headers['Authorization'] = `Bearer ${auth.accessToken}`;
        }

        return config;
    };

    const isTokenValid = (expiryTime: string): boolean => {
        return Math.abs(moment.utc(expiryTime).diff(moment.utc(), 'minutes')) > 2;
    };

    const handleRequestError = (error: any) => {
        return Promise.reject(error);
    }
    //#endregion

    //#region Response helpers
    const handleSuccessResponse = (response: any) => {
        response.data.statusCode = response.status;

        const shouldPassthrough = PASS_THROUGH_URLS.some(route =>
            response.config.url?.startsWith(route)
        );

        if (shouldPassthrough) return response;

        return response.data;
    };

    const handleErrorResponseAsync = async (error: any) => {
        if (error?.request?.responseType === "blob")
            await convertBlobErrorToJsonAsync(error);

        const apiError = prepareErrorResponse(error);
        const url = error.config?.url;

        //Error showing handled on component
        if (url === API_ROUTE_SIGNIN || url.startsWith("auth/verifyemail/"))
            return apiError;

        //This will be handled by session timeout logic in the AuthProvider
        //Status unauthorized - show common error and redirect user to login page after removing auth
        if (apiError.statusCode === 401 && !ANNONYMUS_API_ROUTES.includes(url)) {
            AuthStore.removeAuth();
            return apiError;
        }

        //Rest of the APIs, show common error handling UI
        showErrorUI(apiError);

        return apiError;
    };

    const convertBlobErrorToJsonAsync = async (error: any) => {
        try {
            const text = await error?.response?.data.text();
            error.response.data = JSON.parse(text);
            error.response.data.statusCode = error?.response?.status ?? 500;
        } catch {
            error.response.data = {
                isSuccess: false,
                message: "Something went wrong. Please try again.",
                statusCode: error?.response?.status ?? 500,
            };
        }
    };

    const prepareErrorResponse = (error: any): IAPIResult => {
        if (
            error?.code === "ERR_CANCELED" ||
            error?.name === "CanceledError" ||
            error?.name === "AbortError"
        ) {
            return {
                isSuccess: false,
                statusCode: 0,
                message: "",
                data: null
            };
        }

        const res: IAPIResult = {
            isSuccess: false,
            statusCode: error?.response?.status ?? 0,
            message: '',
            data: error?.response?.data?.data,
            errorCode: error?.response?.data?.errorCode
        };

        if (error.code === "ERR_NETWORK") {
            res.message = 'Network issue. There might be some issue with the network. Please check the internet connection.';
        } else if (error.response) {
            let messageFromAPI = error.response.data?.message;
            switch (error.response.status) {
                case 400:
                    res.message = messageFromAPI ?? 'Request error. Please check the input data.';
                    if (error.response.data?.data && Array.isArray(error.response.data?.data)) {
                        const list = error.response.data.data.map(
                            (e: any) => `<li><b>${e.field}:</b> ${e.messages.join(", ")}</li>`
                        ).join('');
                        res.message += `<ul class='error-modal-list'>${list}</ul>`;
                    }
                    break;
                case 401:
                    res.message = messageFromAPI ?? 'Unauthorized access. You are not authorized to view the requested resource.';
                    break;
                case 403:
                    res.message = (messageFromAPI ?? 'You do not have an access to the resource you are trying to access.') + (res.errorCode ? ` Error Code: <b>${res.errorCode}</b>` : "");
                    break;
                case 404:
                    res.message = (messageFromAPI ?? 'The resource you are trying to access is not found.') + (res.errorCode ? ` Error Code: <b>${res.errorCode}</b>` : "");
                    break;
                case 405:
                    res.message = (messageFromAPI ?? 'Method not found exception occurred.') + (res.errorCode ? ` Error Code: <b>${res.errorCode}</b>` : "");
                    break;
                case 415:
                case 500:
                    let isException = false;
                    if (messageFromAPI && (CommonHelper.environment.isDevelopment() || CommonHelper.environment.isLocal())) {
                        console.log("Error message from API:", messageFromAPI);
                        const escapeHtml = (str: string) => {
                            if (typeof str !== 'string') return str;
                            return str
                                .replace(/&/g, "&amp;")
                                .replace(/</g, "&lt;")
                                .replace(/>/g, "&gt;")
                                .replace(/"/g, "&quot;")
                                .replace(/'/g, "&#039;");
                        }

                        messageFromAPI = messageFromAPI.replace(
                            /<<<([^>]+)>>>[\r\n]+((?:.*(?:\r?\n)?)*?)(?=(<<<|$))/g,
                            (_: any, title: string, value: string) => {
                                const escapedTitle = escapeHtml(title.trim());
                                const escapedValue = escapeHtml(value.trim()).replace(/\r?\n/g, "<br/>");
                                return `<div class='title-text'><b>${escapedTitle}</b></div><div class='content-text'>${escapedValue}</div>`;
                            }
                        );

                        if (res.errorCode)
                            messageFromAPI = `<div class='title-text'><b>Error Code</b></div><div class='content-text'>${res.errorCode}</div>` + messageFromAPI;
                        isException = true;
                    }
                    else
                        messageFromAPI = (messageFromAPI ?? 'Something went wrong on our end. Please try again.') + (res.errorCode ? ` Error Code: <b>${res.errorCode}</b>` : "");

                    res.message = messageFromAPI;
                    (res as any).isForException = isException;
                    break;
                default:
                    res.message = (messageFromAPI ?? error.response.statusText ?? 'An unexpected error occurred.') + + (res.errorCode ? ` Error Code: <b>${res.errorCode}</b>` : "");
            }
        } else if (error.request) {
            res.message = 'No response received from the server. Please check your connection.';
        } else if (error.message) {
            res.message = `${error.message}`;
        } else {
            res.message = `An unexpected error occurred.`;
        }

        if (axios.isAxiosError(error) && error.response)
            res.message = res.message ?? 'An error occurred while processing your request.';

        return res;
    };

    const showErrorUI = (res: IAPIResult): void => {
        if (!res.message) return;

        switch (res.statusCode) {
            case 400:
                AlertDialog.show({
                    type: "danger",
                    title: "Request error",
                    content: res.message
                });
                break;
            case 403:
                AlertDialog.show({
                    type: "danger",
                    title: "Forbidden access",
                    content: res.message
                });
                break;
            case 404:
                AlertDialog.show({
                    type: "danger",
                    title: "Not found",
                    content: res.message
                });
                break;
            case 405:
            case 415:
            case 500:
                AlertDialog.show({
                    type: "danger",
                    title: "Server error",
                    content: res.message,
                    isForException: (res as any).isForException ?? false
                });
                break;
            default:
                Toaster.error(res.message);
                break;
        }
    };
    //#endregion

    // Request interceptor
    axiosInstance.interceptors.request.use(
        attachAuthHeader,
        handleRequestError
    );

    // Response interceptor
    axiosInstance.interceptors.response.use(
        handleSuccessResponse,
        handleErrorResponseAsync
    );

    return axiosInstance;
};

export default GetITBAxiosInstance;